import { z } from 'zod';
import { uuidSchema } from '../schema/rows.js';
import { blockKindSchema, stageCategorySchema, stageKindScopeSchema, } from '../template/manifest.js';
import { triageItem } from './triage.js';
import { DomainError } from './types.js';
/**
 * «Mover a…» from a list (docs/05 §6.1, design D4 of f1-asap-view): the same stage change as the
 * triage (A/B/G and S with its follow-up data), plus the withdrawal of the block's highlights
 * when it leaves their eligible category (docs/04 §4 rule 2). Returned with its undo.
 */
const removal = (id, removedAt) => ({
    table: 'highlights',
    op: 'PATCH',
    id: id,
    data: { removed_at: removedAt },
});
export const highlightEligibility = z.object({
    id: uuidSchema,
    eligible_stage_category: stageCategorySchema,
    /** Kind the highlight requires; absent when only the stage changes. */
    eligible_kind: blockKindSchema.optional(),
});
/**
 * Highlights of a block that stop being eligible once it has this kind and stage category
 * (docs/04 §4 rule 2, INV-07): withdrawn with `removed_at`, restored by the undo. Shared by the
 * stage change and the type conversion (design D3 of f1-type-conversion).
 */
export function withdrawIneligible(highlights, target, now) {
    const gone = highlights
        .map((h) => highlightEligibility.parse(h))
        .filter((h) => h.eligible_stage_category !== target.category ||
        (h.eligible_kind !== undefined && h.eligible_kind !== target.kind));
    return {
        // Withdraw first: a removal is not checked by the server, the block then changes.
        before: gone.map((h) => removal(h.id, now)),
        // The undo puts the block back first, then the highlight returns (the server checks INV-07).
        after: gone.map((h) => removal(h.id, null)),
        withdrawn: gone.length,
    };
}
const setStageInput = z.object({
    /** The move itself, as a triage decision on the block (`stage` or `waiting`). */
    move: z.custom(),
    decision: z.custom(),
    /** Active highlights of the block, and the category they require. */
    highlights: z.array(highlightEligibility).default([]),
});
export function setStage(input) {
    const p = setStageInput.parse(input);
    if (p.decision.type !== 'stage' && p.decision.type !== 'waiting') {
        throw new Error('setStage solo mueve a una etapa (stage o waiting).');
    }
    const now = p.move.now ?? new Date().toISOString();
    const moved = triageItem({ ...p.move, decision: p.decision, now });
    const withdrawal = withdrawIneligible(p.highlights, { kind: p.move.item.kind, category: p.decision.stage.category }, now);
    return {
        operations: [...withdrawal.before, ...moved.operations],
        undo: [...moved.undo, ...withdrawal.after],
        withdrawn: withdrawal.withdrawn,
    };
}
const setProjectStageInput = z.object({
    block: z.object({
        id: uuidSchema,
        kind: blockKindSchema,
        stage_id: uuidSchema.nullable(),
        stage_changed_at: z.string().nullable(),
    }),
    stage: z.object({ id: uuidSchema, kind_scope: stageKindScopeSchema }),
    now: z.string().optional(),
});
/**
 * «Mover a…» of a project, from the detail panel (f1-type-conversion D5): only among the stages
 * of projects (INV-04). The triage rules of `setStage` are for tasks; a project has no highlight.
 */
export function setProjectStage(input) {
    const p = setProjectStageInput.parse(input);
    if (p.block.kind !== 'project' || p.stage.kind_scope !== 'project') {
        throw new DomainError('invariant_violation', 'Un proyecto solo va a etapas de proyecto.', 'INV-04');
    }
    if (p.stage.id === p.block.stage_id)
        return { operations: [], undo: [] };
    const id = p.block.id;
    return {
        operations: [
            {
                table: 'blocks',
                op: 'PATCH',
                id,
                data: { stage_id: p.stage.id, stage_changed_at: p.now ?? new Date().toISOString() },
            },
        ],
        undo: [
            {
                table: 'blocks',
                op: 'PATCH',
                id,
                data: { stage_id: p.block.stage_id, stage_changed_at: p.block.stage_changed_at },
            },
        ],
    };
}
//# sourceMappingURL=stage.js.map